import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { AlertCircle, CheckCircle2, Package } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

export interface FinalizeReceptionModalRef {
  open: (
    receptionCode: string,
    totalOrders: number,
    processedOrders: number,
    onConfirm: () => Promise<void>,
  ) => void;
}

export const FinalizeReceptionModal = forwardRef<FinalizeReceptionModalRef>((_, ref) => {
  const modalRef = useRef<ModalCenterRef>(null);
  const [receptionCode, setReceptionCode] = useState<string>('');
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [processedOrders, setProcessedOrders] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const confirmCallbackRef = useRef<(() => Promise<void>) | null>(null);

  useImperativeHandle(ref, () => ({
    open: (code: string, total: number, processed: number, onConfirm: () => Promise<void>) => {
      setReceptionCode(code);
      setTotalOrders(total);
      setProcessedOrders(processed);
      setIsProcessing(false);
      confirmCallbackRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = async () => {
    if (confirmCallbackRef.current) {
      setIsProcessing(true);
      try {
        await confirmCallbackRef.current();
        modalRef.current?.closeModal();
      } catch (error) {
        console.error('Error al finalizar recepción:', error);
        modalRef.current?.closeModal();
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      modalRef.current?.closeModal();
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title="Finalizar Recepción"
      type="warning"
      width="lg"
      onCloseModal={() => setIsProcessing(false)}
    >
      <div className="space-y-6">
        {/* Header con información */}
        <div className="bg-muted/30 rounded-xl p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{receptionCode}</h3>
              <p className="text-sm text-muted-foreground">
                {processedOrders} de {totalOrders} pedidos procesados
              </p>
            </div>
          </div>
        </div>

        {/* Advertencia importante */}
        <Alert className="bg-orange-50 dark:bg-orange-950/20 border-2 border-orange-300 dark:border-orange-700">
          <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          <AlertDescription className="text-sm text-orange-700 dark:text-orange-300">
            <strong>¡Importante!</strong> Al finalizar esta recepción:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>No podrás procesar más pedidos en esta recepción</li>
              <li>Todas las líneas aceptadas deben tener orden de ubicación asignada</li>
              <li>
                Esta acción <strong>no se puede deshacer</strong>
              </li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Confirmación */}
        <div className="bg-muted/20 rounded-lg p-4 border border-border">
          <p className="text-sm text-foreground text-center">¿Estás seguro de que deseas finalizar esta recepción?</p>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" size="lg" onClick={handleClose} disabled={isProcessing} className="min-w-[120px]">
            Cancelar
          </Button>
          <Button
            size="lg"
            onClick={handleConfirm}
            disabled={isProcessing}
            className="min-w-[180px] bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2"></div>
                Finalizando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Confirmar Finalización
              </>
            )}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

FinalizeReceptionModal.displayName = 'FinalizeReceptionModal';
